/**
 * Latency calibration flow (spec 5.4): a metronome plays a 4-beat count-in
 * plus 16 scored beats at 120 BPM with a pulsing circle; the user taps along;
 * the median tap−beat difference (clamped ±250 ms) becomes the device's
 * latency offset.
 *
 * Clicks are scheduled on the AudioContext timeline via 25 ms look-ahead (the
 * "Tale of Two Clocks" pattern); the circle pulse is driven from rAF by
 * comparing against ctx.currentTime.
 */
import {
  CALIBRATION_BEATS,
  CALIBRATION_BPM,
  CALIBRATION_COUNT_IN,
  SCHEDULER_HORIZON_S,
  SCHEDULER_INTERVAL_MS,
} from "../config/tuning";
import type { AudioSystem } from "../audio/audioSystem";
import { computeLatencyOffsetMs, nearestBeatDiffMs } from "../audio/calibrationMath";

const SPB = 60 / CALIBRATION_BPM;
const MATCH_WINDOW_MS = (SPB / 2) * 1000;

export class CalibrationFlow {
  private interval: ReturnType<typeof setInterval> | null = null;
  private raf = 0;
  private beatTimes: number[] = []; // scored beats, ctx timeline
  private diffs: number[] = [];
  private nextBeatIdx = 0;
  private startCtxTime = 0;
  private outputLatencyS = 0;
  private done = false;

  constructor(
    private readonly audio: AudioSystem,
    private readonly els: { circle: HTMLElement; status: HTMLElement },
    private readonly onDone: (offsetMs: number | null) => void,
  ) {}

  start(): void {
    const ctx = this.audio.ensureUnlocked();
    if (!ctx) {
      this.finish(null);
      return;
    }
    const anyCtx = ctx as AudioContext & { outputLatency?: number };
    this.outputLatencyS = (anyCtx.outputLatency ?? 0) + ctx.baseLatency;
    this.startCtxTime = ctx.currentTime + 0.5;
    this.nextBeatIdx = 0;
    this.beatTimes = [];
    this.diffs = [];
    this.done = false;

    // Look-ahead click scheduler
    this.interval = setInterval(() => {
      const total = CALIBRATION_COUNT_IN + CALIBRATION_BEATS;
      while (this.nextBeatIdx < total) {
        const t = this.startCtxTime + this.nextBeatIdx * SPB;
        if (t > ctx.currentTime + SCHEDULER_HORIZON_S) break;
        const isCountIn = this.nextBeatIdx < CALIBRATION_COUNT_IN;
        this.audio.click(t, this.nextBeatIdx % 4 === 0);
        if (!isCountIn) this.beatTimes.push(t);
        this.nextBeatIdx++;
      }
    }, SCHEDULER_INTERVAL_MS);

    // Circle pulse from rAF vs the audio clock
    const pulse = (): void => {
      const now = ctx.currentTime;
      const sinceStart = now - this.startCtxTime;
      const phase = ((sinceStart % SPB) + SPB) % SPB;
      this.els.circle.classList.toggle("beat", sinceStart >= 0 && phase < 0.12);
      if (sinceStart < 0) {
        this.els.status.textContent = "Get ready…";
      } else if (sinceStart < CALIBRATION_COUNT_IN * SPB) {
        this.els.status.textContent = "Listen…";
      } else if (!this.done) {
        this.els.status.textContent = `Tap with the beat — ${this.diffs.length}/${CALIBRATION_BEATS}`;
        // Out of beats and grace period over → wrap up with what we have
        const lastBeat = this.startCtxTime + (CALIBRATION_COUNT_IN + CALIBRATION_BEATS) * SPB;
        if (now > lastBeat + 1.0) this.finish(computeLatencyOffsetMs(this.diffs));
      }
      if (!this.done) this.raf = requestAnimationFrame(pulse);
    };
    this.raf = requestAnimationFrame(pulse);
  }

  /** Call from pointerdown / Space keydown while calibrating. */
  tap(): void {
    const ctx = this.audio.context;
    if (!ctx || this.done) return;
    const diff = nearestBeatDiffMs(
      ctx.currentTime - this.outputLatencyS,
      this.beatTimes,
      MATCH_WINDOW_MS,
    );
    if (diff !== null) {
      this.diffs.push(diff);
      if (this.diffs.length >= CALIBRATION_BEATS) {
        this.finish(computeLatencyOffsetMs(this.diffs));
      }
    }
  }

  cancel(): void {
    this.finish(null);
  }

  private finish(offsetMs: number | null): void {
    if (this.done) return;
    this.done = true;
    if (this.interval !== null) clearInterval(this.interval);
    cancelAnimationFrame(this.raf);
    this.els.circle.classList.remove("beat");
    this.onDone(offsetMs);
  }
}
